import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  standardTransition,
  zIndexLayers,
} from '../styles';
import { Button, Link, Svg } from './ui';
import loginIcon from '../images/login-icon.inline.svg';

const StyledNavigation = styled.nav`
  display: flex;
`;

const StyledHighlightedLink = styled(Button)`
  padding: 10px 16px;
  margin: auto 0;
  margin-right: 10px;
  ${fontSize(14)};
  border-radius: 10px;

  ${minBreakpointQuery.tiny`
    padding: 15px 24px;
  `}

  ${minBreakpointQuery.tsmall`
    margin-right: 15px;
    padding-left: 32px;
    padding-right: 32px;
    ${fontSize(15)};
  `}

  ${minBreakpointQuery.small`
    margin-right: 20px;
    padding-left: 40px;
    padding-right: 40px;
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.mlarge`
    padding-top: 25px;
    padding-bottom: 25px;
    ${fontSize(17)};
  `}

  ${minBreakpointQuery.large`
    margin-right: 30px;
    padding-left: 56px;
    padding-right: 56px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-right: 40px;
    padding-left: 64px;
    padding-right: 64px;
  `}

  ${minBreakpointQuery.xxlarge`
    padding-left: 72px;
    padding-right: 72px;
  `}

  &:hover {
    background-color: ${brandColours.secondary};
  }
`;

const StyledToggle = styled.button`
  ${maxBreakpointQuery.mlarge`
    position: relative;
    margin: 0;
    padding: 25px 0;
    background-color: ${standardColours.lightGrey};
    border: none;
  `}

  ${maxBreakpointQuery.small`
    padding: 20px 0;
  `}

  ${minBreakpointQuery.mlarge`
    display: none;
  `}
  
  span {
    ${maxBreakpointQuery.mlarge`
      display: block;
      height: 2px;
      background-color: ${standardColours.darkGrey};
      transition: ${standardTransition('transform')};
      width: 28px;
      margin: 6px 22px;

      ${({ $displayNav }) => {
        if ($displayNav) {
          return css`
            &:first-child {
              transform: translateY(4px) rotate(45deg);
            }

            &:last-child {
              transform: translate(0, -4px) rotate(-45deg);
            }
          `;
        }
      }}
    `}

    ${maxBreakpointQuery.tsmall`
      width: 24px;
      margin: 6px 19px;
    `}
  }
`;

const StyledList = styled.ul`
  text-align: center;

  ${maxBreakpointQuery.mlarge`
    position: absolute;
    top: 72px;
    right: 0;
    left: 0;
    z-index: ${zIndexLayers.fourth};
    display: ${({ $displayNav }) => ($displayNav ? 'block' : 'none')};
    padding-top: 60px;
    padding-bottom: 60px;
    background-color: ${standardColours.white};
    height: 100%;
  `}

  ${maxBreakpointQuery.small`
    top: 62px;
  `}

  ${minBreakpointQuery.mlarge`
    display: flex;
    gap: 20px;
  `}

  ${minBreakpointQuery.large`
    gap: 30px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 40px;
  `}
`;

const StyledItem = styled.li``;

const StyledLink = styled(Link)`
  ${fontSize(17)};

  ${maxBreakpointQuery.mlarge`
    display: block;
    padding: 12px 30px;
  `}

  ${minBreakpointQuery.mlarge`
    position: relative;
    display: flex;
    align-items: center;
    height: 100%;
  `}

  &:after {
    ${minBreakpointQuery.mlarge`
      content: '';
      position: absolute;
      background-color: ${standardColours.transparent};
      width: 100%;
      height: 3px;
      bottom: 0;
      left: 0;
      transition: ${standardTransition('background-color')};
    `}
  }

  &.current-page {
    font-weight: ${fontWeights.semibold};
  }

  &:hover {
    &:after {
      ${minBreakpointQuery.mlarge`
        background-color: ${standardColours.darkGrey};
      `}
    }
  }
`;

const StyledLoginLink = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  ${fontSize(17)};
  white-space: nowrap;

  ${maxBreakpointQuery.mlarge`
    padding: 12px 30px;
  `}

  ${minBreakpointQuery.mlarge`
    margin-left: 10px;
    padding: 40px 25px;
    height: 100%;
    background-color: ${standardColours.lightGrey};
  `}

  ${minBreakpointQuery.large`
    padding-right: 35px;
    padding-left: 35px;
  `}

  ${minBreakpointQuery.xxlarge`
    padding-right: 45px;
    padding-left: 45px;
  `}

  &.current-page {
    font-weight: ${fontWeights.semibold};
  }
`;

const Navigation = ({ displayNav, setDisplayNav, ...props }) => {
  const {
    contentfulSitewide: { headerLinks, headerLogInLink, headerHighlightedLink },
  } = useStaticQuery(graphql`
    query NavigationQuery {
      contentfulSitewide {
        headerLinks {
          text
          pageUrl {
            ...LinkFragment
          }
        }
        headerLogInLink {
          ...LinkFragment
        }
        headerHighlightedLink {
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
    }
  `);

  return (
    <StyledNavigation {...props}>
      <StyledHighlightedLink to={headerHighlightedLink.pageUrl} isAlt={true}>
        {headerHighlightedLink.text}
      </StyledHighlightedLink>
      <StyledToggle
        aria-label="Open / Close Navigation"
        onClick={() => {
          setDisplayNav(!displayNav);
          window.scrollTo({ top: 0 });
        }}
        $displayNav={displayNav}
      >
        <span />
        <span />
      </StyledToggle>
      <StyledList $displayNav={displayNav}>
        {headerLinks.map(({ text, pageUrl }, id) => (
          <StyledItem key={id}>
            <StyledLink to={pageUrl} activeClassName="current-page">
              {text}
            </StyledLink>
          </StyledItem>
        ))}
        <StyledItem>
          <StyledLoginLink
            href={headerLogInLink.url}
            target="_blank"
            rel="noopener"
          >
            <Svg image={loginIcon} />
            Log in
          </StyledLoginLink>
        </StyledItem>
      </StyledList>
    </StyledNavigation>
  );
};

export default Navigation;
